import { SectionShell } from "@/components/design-system/SectionShell";
import { GradeBadge } from "@/components/ui/grade-badge";
import { FindingCard } from "@/components/ui/finding-card";
import { FindingGroup } from "@/components/ui/finding-group";

const code = `import { GradeBadge } from "@/components/ui/grade-badge";
import { FindingCard } from "@/components/ui/finding-card";
import { FindingGroup } from "@/components/ui/finding-group";

<GradeBadge grade="B" size="lg" />

<FindingGroup status="fail" title="What's broken (2)">
  <FindingCard
    status="fail"
    title="HTTPS / SSL"
    body="Your certificate is invalid or expired."
    detail="Browsers will show a security warning to every visitor."
  />
</FindingGroup>`;

export function PodcastFindingsSection() {
  return (
    <SectionShell
      id="findings"
      title="Findings (grade badge, finding card, finding group)"
      description={
        <>
          The audit-report trio: a circular <code>GradeBadge</code>, a
          left-accented <code>FindingCard</code>, and a <code>FindingGroup</code>{" "}
          section wrapper. Extracted from the SEO audit results page and
          generalized to serve any pass/warn/fail report (SEO, podcast
          discoverability, etc.). Severity maps to the status tones:{" "}
          <code>success</code> for pass, <code>signal</code> for warn, and{" "}
          <code>danger</code> for fail. The matching <code>Badge</code> tones
          are available for the same states in tables and admin views.
        </>
      }
      whenToUse={
        <ul>
          <li>Automated audit or scorecard reports graded A-F.</li>
          <li>Any list of pass/warn/fail findings grouped by severity.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Simple status tags — use <code>Badge</code> instead.</li>
          <li>Free-form content callouts — use <code>.callout</code>.</li>
        </ul>
      }
      preview={
        <div className="space-y-8">
          <div className="flex flex-wrap items-center gap-4">
            <GradeBadge grade="A" size="sm" />
            <GradeBadge grade="B" size="md" />
            <GradeBadge grade="C" size="md" />
            <GradeBadge grade="F" size="lg" />
          </div>

          <FindingGroup status="fail" title="What's broken (1)">
            <FindingCard
              status="fail"
              title="HTTPS / SSL"
              body="Your certificate is invalid or expired."
              detail="Browsers will show a security warning to every visitor."
            />
          </FindingGroup>
          <FindingGroup status="warn" title="Worth fixing (1)">
            <FindingCard
              status="warn"
              title="Subtitle"
              body="Your subtitle is byte-identical to the start of your description."
            />
          </FindingGroup>
          <FindingGroup status="pass" title="What's working (1)">
            <FindingCard status="pass" title="Categories" body="Using all 3 category slots." />
          </FindingGroup>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>GradeBadge</code> — <code>grade</code>: any letter grade
            string (A-F drive the tone; anything else falls back to the
            "fair"/amber tone). <code>size</code>: <code>sm</code> |{" "}
            <code>md</code> (default) | <code>lg</code>.
          </li>
          <li>
            <code>FindingCard</code> — <code>status</code>:{" "}
            <code>fail</code> | <code>warn</code> | <code>pass</code>{" "}
            (default). <code>title</code>/<code>body</code> are required;{" "}
            <code>detail</code> is an optional smaller muted line underneath.
          </li>
          <li>
            <code>FindingGroup</code> — <code>status</code> sets the eyebrow
            tone; <code>title</code> is any node (fold the count into the
            title text, e.g. <code>{"`What's broken (${fails.length})`"}</code>).
          </li>
        </ul>
      }
    />
  );
}
